// ── Response types (mirror KollaborateServer/src/models/server.rs) ──
//
// The endpoint has no serde attributes on these structs, so field names arrive
// verbatim in snake_case and Option fields arrive as `null` rather than being
// omitted.

export interface JobStatusItem {
  job_id: number;
  /** The queue's own scheduling key, e.g. `encoding-1234`. */
  unique_id: string;
  file_id: number;
  file_type_id: number;
  file_title: string;
  /** Overall progress through the whole job, 0-100. */
  progress_percent: number;
  /** `downloading`, `processing` or `uploading`. */
  mode: string;
  /**
   * Frame rate the file is currently being processed at. `0` when the job
   * isn't reporting one - not every mode or job type produces it.
   */
  fps: number;
  failures: number;
  last_error: string | null;
  /**
   * How long ago the job finished, in seconds. Null while pending or running.
   * The server has no usable absolute timestamp to send (the processor's
   * `Instant` has no meaning across processes), so this is an age measured at
   * the moment the request was served.
   */
  finished_seconds_ago: number | null;
}

export interface QueueStatus {
  pending: JobStatusItem[];
  running: JobStatusItem[];
  successful: JobStatusItem[];
  failed: JobStatusItem[];
}

/** Body of `GET /admin/servers/{server_id}/queues`, keyed by queue name. */
export type QueuesResponse = Record<string, QueueStatus>;

/** A job flattened out of its queue, tagged with the queue it came from. */
export type QueueJob = JobStatusItem & { queueName: string };

/** Which columns a section renders. */
export type QueueSectionVariant = 'running' | 'failed' | 'successful' | 'pending';

export interface ServerQueuesProps {
  serverId: number;
  /** Poll interval in ms. Defaults to 5000. */
  pollInterval?: number;
}

export interface ServerQueuesHandle {
  type: 'server-queues';
  unmount: () => void;
}

// ── Job logs ──

/** Body of `GET /admin/servers/{server_id}/jobs/log-count`. */
export interface JobLogCountResponse {
  log_count: number;
}

/** Body of `GET /admin/servers/{server_id}/jobs/log-data`. */
export interface JobLogDataResponse {
  data: string;
}

export interface JobLogViewerProps {
  serverId: number;
  queueName: string;
  jobId: number;
}

/** The job whose logs the dialog is showing. Null when it's closed. */
export interface JobLogTarget {
  queueName: string;
  jobId: number;
  /** Shown in the dialog's title bar. */
  title: string;
}

/**
 * Sections whose jobs can have logs. A pending job has never been spawned, so
 * the processor has never opened a file for it. COLUMNS and JobRow both have to
 * agree on this, so it lives in one place.
 */
export const VARIANTS_WITH_LOGS: readonly QueueSectionVariant[] = [
  'running',
  'failed',
  'successful',
];
